import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Building2, CalendarCheck, Check, ChevronLeft, ChevronRight, MapPin, Plus, Video, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatShortTime } from '../lib/format';
import {
  LOOK_CHIP,
  LOOK_DOT,
  LOOK_LABEL,
  addDays,
  addMonths,
  dayKey,
  layoutDay,
  meetingLook,
  monthDays,
  onDay,
  parseDayKey,
  sameDay,
  startOfDay,
  startOfWeek,
  type MeetingLook,
  type MeetingOptions,
  type MeetingPlace,
  type MeetingRow,
} from '../lib/meetings';
import { refusalText } from '../lib/reach';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Select,
  Spinner,
  statusTone,
  useToast,
} from '../components/ui';

type View = 'month' | 'week' | 'list';
type Scope = 'mine' | 'all';

const LOCALE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };
const HOUR_PX = 48;
const PLACE_ICON: Record<MeetingPlace, typeof Building2> = { OFFICE: Building2, OUTSIDE: MapPin, ONLINE: Video };

/** The company's week starts on Settings › Company's day — Saturday, the Egyptian working week. */
function useWeekStart(): number {
  const { data } = useQuery({
    queryKey: ['settings', 'company.weekStartDay'],
    queryFn: () => api.get<unknown>('/settings/company.weekStartDay'),
    staleTime: 5 * 60_000,
  });
  const day = Number(data);
  return Number.isInteger(day) && day >= 0 && day <= 6 ? day : 6;
}

/** The time a meeting runs, "10:30–11:00", in the reader's language. */
export function meetingTimes(m: { startsAt: string; endsAt: string }, locale: Locale): string {
  return `${formatShortTime(m.startsAt, locale)}–${formatShortTime(m.endsAt, locale)}`;
}

/** "Thu, 22 Oct 2026" — a meeting's day, in the reader's language. */
export function meetingDay(value: string | Date, locale: Locale): string {
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(value),
  );
}

/**
 * Route: `/meetings` — the meeting calendar, under My to-do list (operator,
 * 2026-10-21). A month, a week with its hours, or a list; the invitations waiting
 * for the reader's answer above it. Everyone sees their own meetings; a
 * department's leader may widen it to their people's, an administrator to every
 * meeting — and narrow it to one person's. Where it is and which view are in the
 * address, so going back from a meeting comes back here.
 */
export function MeetingsPage() {
  const { t, locale } = useI18n();
  const { can, ledDepartmentIds } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const weekStart = useWeekStart();

  const manages = can(P.MEETINGS_MANAGE);
  const leads = ledDepartmentIds.length > 0;
  const wider = manages || leads;
  const view: View = params.get('view') === 'week' ? 'week' : params.get('view') === 'list' ? 'list' : 'month';
  const scope: Scope = wider && params.get('scope') === 'all' ? 'all' : 'mine';
  const person = scope === 'all' ? (params.get('person') ?? '') : '';
  const today = startOfDay(new Date());
  const anchor = parseDayKey(params.get('date')) ?? today;

  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  // What the page shows: a month's whole weeks, a week, or the month's days for the list.
  const days =
    view === 'week'
      ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor, weekStart), i))
      : view === 'month'
        ? monthDays(anchor, weekStart)
        : Array.from(
            { length: addDays(addMonths(anchor, 1), -1).getDate() },
            (_, i) => new Date(anchor.getFullYear(), anchor.getMonth(), i + 1),
          );
  const from = days[0];
  const to = addDays(days[days.length - 1], 1);

  const meetings = useQuery({
    queryKey: ['meetings', 'list', from.toISOString(), to.toISOString(), scope, person],
    queryFn: () =>
      api.get<MeetingRow[]>('/meetings', {
        from: from.toISOString(),
        to: to.toISOString(),
        scope,
        userId: person || undefined,
      }),
    placeholderData: (previous) => previous,
  });

  // Whose meetings, for a leader or an administrator looking wider: the people they see.
  const options = useQuery({
    queryKey: ['meetings', 'options'],
    queryFn: () => api.get<MeetingOptions>('/meetings/options'),
    enabled: scope === 'all',
    staleTime: 60_000,
  });

  const step = (direction: 1 | -1) =>
    set({
      date: dayKey(
        view === 'week' ? addDays(startOfWeek(anchor, weekStart), 7 * direction) : addMonths(anchor, direction),
      ),
      day: null,
    });
  // The day picked; else today when it is in view; else the day the address names (a month's first, stepping).
  const selected = parseDayKey(params.get('day')) ?? (days.some((d) => sameDay(d, today)) ? today : anchor);

  const heading =
    view === 'week'
      ? weekLabel(days[0], days[6], locale)
      : new Intl.DateTimeFormat(LOCALE_TAG[locale], { month: 'long', year: 'numeric' }).format(anchor);

  return (
    <>
      <PageHeader
        title={t('meetings.title')}
        subtitle={t(manages ? 'meetings.subtitleAdmin' : leads ? 'meetings.subtitleLeader' : 'meetings.subtitle')}
        action={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate(`/meetings/new?date=${dayKey(selected < today ? today : selected)}`)}
          >
            {t('meetings.new')}
          </Button>
        }
      />

      <Invitations />

      <Card padded={false}>
        {/* Where on the calendar, which view, and whose meetings. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b hairline p-3">
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => set({ date: null, day: null })}>
              {t('meetings.today')}
            </Button>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label={t('meetings.previous')}
              className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
            >
              <ChevronLeft className="h-4 w-4 flip-rtl" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label={t('meetings.next')}
              className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
            >
              <ChevronRight className="h-4 w-4 flip-rtl" aria-hidden />
            </button>
          </div>
          <h2 className="min-w-0 flex-1 truncate text-h3 text-[var(--text-primary)]" aria-live="polite">
            {heading}
            {meetings.isFetching && !meetings.isLoading && <Spinner className="ms-2 inline h-4 w-4" />}
          </h2>
          <Segmented
            label={t('meetings.viewLabel')}
            value={view}
            onChange={(v) => set({ view: v === 'month' ? null : v, day: null })}
            options={[
              { id: 'month', label: t('meetings.view.month') },
              { id: 'week', label: t('meetings.view.week') },
              { id: 'list', label: t('meetings.view.list') },
            ]}
          />
          {wider && (
            <Segmented
              label={t('meetings.scopeLabel')}
              value={scope}
              onChange={(v) => set({ scope: v === 'all' ? 'all' : null, person: null })}
              options={[
                { id: 'mine', label: t('meetings.scope.mine') },
                { id: 'all', label: t(manages ? 'meetings.scope.all' : 'meetings.scope.team') },
              ]}
            />
          )}
          {scope === 'all' && (
            <Select
              value={person}
              onChange={(e) => set({ person: e.target.value || null })}
              aria-label={t('meetings.person')}
              className="h-8 w-auto max-w-[14rem] text-body-sm"
            >
              <option value="">{t('meetings.personAll')}</option>
              {options.data?.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {locale === 'ar' ? p.fullNameAr || p.fullName : p.fullName}
                </option>
              ))}
            </Select>
          )}
        </div>

        {meetings.isLoading ? (
          <LoadingBlock />
        ) : meetings.error ? (
          <ErrorState error={meetings.error} onRetry={() => void meetings.refetch()} />
        ) : view === 'month' ? (
          <MonthView
            days={days}
            anchor={anchor}
            selected={selected}
            meetings={meetings.data ?? []}
            onSelect={(d) => set({ day: dayKey(d) })}
          />
        ) : view === 'week' ? (
          <WeekView days={days} meetings={meetings.data ?? []} />
        ) : (
          <ListView days={days} meetings={meetings.data ?? []} />
        )}
      </Card>

      <Legend wider={wider} />
    </>
  );
}

function weekLabel(first: Date, last: Date, locale: Locale): string {
  const format = (d: Date, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(LOCALE_TAG[locale], opts).format(d);
  return first.getMonth() === last.getMonth()
    ? `${format(first, { day: 'numeric' })}–${format(last, { day: 'numeric', month: 'long', year: 'numeric' })}`
    : `${format(first, { day: 'numeric', month: 'short' })} – ${format(last, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** A few choices side by side, one of them on — the view, and whose meetings. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ id: T; label: ReactNode }>;
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border hairline bg-[var(--surface-sunken)] p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            'whitespace-nowrap rounded-sm px-2.5 py-1 text-body-sm font-medium transition-colors duration-fast',
            value === o.id
              ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-sm'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Waiting for your answer                                                     */
/* -------------------------------------------------------------------------- */

/** The invitations waiting for the reader's answer, above the calendar: accept here, or decline with a word. */
function Invitations() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['meetings', 'invitations'],
    queryFn: () => api.get<{ count: number; items: MeetingRow[] }>('/meetings/invitations'),
    refetchInterval: 60_000,
  });
  const accept = useMutation({
    mutationFn: (id: string) => api.post(`/meetings/${id}/accept`, {}),
    onSuccess: () => {
      toast.success(t('meetings.acceptedToast'));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
    onError: (e: Error) => toast.error(meetingRefusal(e, t)),
  });

  if (!data?.count) return null;
  return (
    <Card className="mb-5 border-warning/40" padded={false}>
      <div className="flex items-baseline justify-between gap-3 px-4 pt-3">
        <h2 className="text-title text-[var(--text-primary)]">
          {t('meetings.waitingTitle')} <span className="text-caption text-[var(--text-tertiary)] tabular">{data.count}</span>
        </h2>
        <p className="hidden text-caption text-muted sm:block">{t('meetings.waitingHint')}</p>
      </div>
      <ul className="divide-y divide-[var(--line)] px-1 pb-1 pt-2">
        {data.items.map((m) => {
          const Icon = PLACE_ICON[m.place];
          return (
            <li key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
              <Link to={`/meetings/${m.id}`} className="min-w-0 flex-1 basis-64 group">
                <span className="block truncate text-body-sm font-medium text-[var(--text-primary)] group-hover:text-primary-600 dark:group-hover:text-primary-400">
                  <bdi>{m.title}</bdi>
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-caption text-muted">
                  <span className="tabular">
                    {meetingDay(m.startsAt, locale)} · {meetingTimes(m, locale)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    {t(`meetings.place.${m.place}` as TranslationKey)}
                  </span>
                  <span>{t('meetings.fromName', { name: localized(m.organizer) })}</span>
                </span>
              </Link>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Check className="h-3.5 w-3.5" />}
                  loading={accept.isPending && accept.variables === m.id}
                  disabled={accept.isPending}
                  onClick={() => accept.mutate(m.id)}
                >
                  {t('meetings.accept')}
                </Button>
                <Button size="sm" icon={<X className="h-3.5 w-3.5" />} onClick={() => navigate(`/meetings/${m.id}/decline`)}>
                  {t('meetings.decline')}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Month                                                                       */
/* -------------------------------------------------------------------------- */

/** As many chips as a day shows before "+2 more". */
const CHIPS_SHOWN = 3;

function MonthView({
  days,
  anchor,
  selected,
  meetings,
  onSelect,
}: {
  days: Date[];
  anchor: Date;
  selected: Date;
  meetings: MeetingRow[];
  onSelect: (day: Date) => void;
}) {
  const { t, locale } = useI18n();
  const today = startOfDay(new Date());
  const weekdays = days.slice(0, 7).map((d) => ({
    short: new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'short' }).format(d),
    narrow: new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'narrow' }).format(d),
  }));
  const ofDay = (day: Date) => meetings.filter((m) => onDay(m, day));

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 lg:border-e hairline">
        <div className="grid grid-cols-7 border-b hairline bg-[var(--surface-sunken)]" aria-hidden>
          {weekdays.map((w, i) => (
            <div key={i} className="px-2 py-1.5 text-center text-overline uppercase text-[var(--text-tertiary)]">
              <span className="hidden sm:inline">{w.short}</span>
              <span className="sm:hidden">{w.narrow}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const list = ofDay(day);
            const outside = day.getMonth() !== anchor.getMonth();
            const isToday = sameDay(day, today);
            const isSelected = sameDay(day, selected);
            const label = new Intl.DateTimeFormat(LOCALE_TAG[locale], { dateStyle: 'full' }).format(day);
            return (
              <div
                key={dayKey(day)}
                onClick={() => onSelect(day)}
                className={clsx(
                  'relative min-h-[4.25rem] cursor-pointer border-b border-e hairline p-1 transition-colors duration-fast sm:min-h-[7.5rem] sm:p-1.5',
                  '[&:nth-child(7n)]:border-e-0',
                  outside && 'bg-neutral-500/5',
                  isSelected ? 'bg-primary/8' : 'hover:bg-[var(--surface-sunken)]',
                )}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(day);
                  }}
                  aria-pressed={isSelected}
                  aria-label={`${label} — ${t('meetings.dayCount', { n: list.length })}`}
                  className={clsx(
                    'grid h-6 min-w-6 place-items-center rounded-full px-1 text-caption font-semibold tabular',
                    isToday
                      ? 'bg-primary text-white'
                      : outside
                        ? 'text-[var(--text-tertiary)]'
                        : 'text-[var(--text-primary)]',
                    isSelected && !isToday && 'ring-1 ring-primary',
                  )}
                >
                  {day.getDate()}
                </button>
                {/* A phone's month has room for dots only; the day's list is below it. */}
                {list.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-0.5 sm:hidden" aria-hidden>
                    {list.slice(0, 4).map((m) => (
                      <span key={m.id} className={clsx('h-1.5 w-1.5 rounded-full', LOOK_DOT[meetingLook(m)])} />
                    ))}
                  </div>
                )}
                <ul className="mt-1 hidden space-y-0.5 sm:block">
                  {list.slice(0, CHIPS_SHOWN).map((m) => (
                    <li key={m.id}>
                      <MeetingChip meeting={m} />
                    </li>
                  ))}
                  {list.length > CHIPS_SHOWN && (
                    <li className="px-1 text-caption font-medium text-muted">
                      {t('meetings.more', { n: list.length - CHIPS_SHOWN })}
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      <DayAgenda day={selected} meetings={ofDay(selected)} />
    </div>
  );
}

/** "Title · 10:30–11:00 · You accepted · In progress" — a chip's tooltip; a scheduled meeting's status goes unsaid. */
function chipTitle(m: MeetingRow, look: MeetingLook, locale: Locale, t: (key: TranslationKey) => string): string {
  const status = m.status !== 'SCHEDULED' && m.status !== 'CANCELLED' ? ` · ${t(`meetings.status.${m.status}` as TranslationKey)}` : '';
  return `${m.title} · ${meetingTimes(m, locale)} · ${t(LOOK_LABEL[look])}${status}`;
}

/** One meeting on a day of the month: its time and title, coloured by what the reader said. */
function MeetingChip({ meeting }: { meeting: MeetingRow }) {
  const { t, locale } = useI18n();
  const look = meetingLook(meeting);
  return (
    <Link
      to={`/meetings/${meeting.id}`}
      onClick={(e) => e.stopPropagation()}
      title={chipTitle(meeting, look, locale, t)}
      className={clsx(
        'flex min-w-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] leading-4 ring-1 ring-inset',
        'transition-[filter] duration-fast hover:brightness-95 dark:hover:brightness-125',
        LOOK_CHIP[look],
      )}
    >
      <span className="shrink-0 font-semibold tabular">{formatShortTime(meeting.startsAt, locale)}</span>
      <bdi className="truncate">{meeting.title}</bdi>
    </Link>
  );
}

/** The day picked on the month: its meetings in full, and a new one on it. */
function DayAgenda({ day, meetings }: { day: Date; meetings: MeetingRow[] }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const past = day < startOfDay(new Date());
  return (
    <section className="min-w-0 border-t hairline p-4 lg:border-t-0" aria-label={t('meetings.dayAgenda')}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-title text-[var(--text-primary)]">
          {new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'long', day: 'numeric', month: 'long' }).format(day)}
        </h3>
        {!past && (
          <Button size="sm" variant="ghost" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => navigate(`/meetings/new?date=${dayKey(day)}`)}>
            {t('meetings.newShort')}
          </Button>
        )}
      </div>
      {meetings.length === 0 ? (
        <p className="py-6 text-center text-body-sm text-muted">{t('meetings.noneDay')}</p>
      ) : (
        <ul className="space-y-2">
          {meetings.map((m) => (
            <li key={m.id}>
              <MeetingCard meeting={m} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A meeting in a list: when, what, where, who called it and how the answers stand. */
function MeetingCard({ meeting, showDay = false }: { meeting: MeetingRow; showDay?: boolean }) {
  const { t, locale, localized } = useI18n();
  const look = meetingLook(meeting);
  const Icon = PLACE_ICON[meeting.place];
  return (
    <Link
      to={`/meetings/${meeting.id}`}
      className="block rounded-md border hairline p-3 transition-colors duration-fast hover:border-primary/50 hover:bg-[var(--surface-sunken)]"
    >
      <div className="flex items-start gap-2.5">
        <span className={clsx('mt-1.5 h-2 w-2 shrink-0 rounded-full', LOOK_DOT[look])} aria-hidden />
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'truncate text-body-sm font-medium text-[var(--text-primary)]',
              look === 'cancelled' && 'line-through text-[var(--text-tertiary)]',
            )}
          >
            <bdi>{meeting.title}</bdi>
          </p>
          <p className="mt-0.5 text-caption text-muted tabular">
            {showDay && <>{meetingDay(meeting.startsAt, locale)} · </>}
            {meetingTimes(meeting, locale)}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-caption text-muted">
            <span className="inline-flex items-center gap-1">
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {meeting.location && meeting.place !== 'ONLINE' ? (
                <bdi className="max-w-[12rem] truncate">{meeting.location}</bdi>
              ) : (
                t(`meetings.place.${meeting.place}` as TranslationKey)
              )}
            </span>
            {meeting.role !== 'ORGANIZER' && <span>{t('meetings.fromName', { name: localized(meeting.organizer) })}</span>}
          </p>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption">
            <span className={clsx('rounded-pill px-2 py-0.5 ring-1 ring-inset', LOOK_CHIP[look])}>{t(LOOK_LABEL[look])}</span>
            {/* Where it stands, once it is under way or done; a scheduled one says nothing more. */}
            {meeting.status !== 'SCHEDULED' && meeting.status !== 'CANCELLED' && (
              <Badge tone={statusTone(meeting.status)} dot={meeting.status === 'IN_PROGRESS'}>
                {t(`meetings.status.${meeting.status}` as TranslationKey)}
              </Badge>
            )}
            {meeting.status !== 'CANCELLED' && meeting.answers.total > 0 && (
              <span className="text-faint tabular">
                {t('meetings.answersShort', { accepted: meeting.answers.accepted, total: meeting.answers.total })}
              </span>
            )}
          </p>
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Week                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A week with its hours: each meeting where it falls in its day, those at the
 * same time side by side. It opens on the working morning; an empty hour starts a
 * new meeting there. Wider than a phone, it scrolls sideways inside its card.
 */
function WeekView({ days, meetings }: { days: Date[]; meetings: MeetingRow[] }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());
  const today = startOfDay(now);

  useEffect(() => {
    // The working morning in view: 08:00, or earlier for the first meeting of the week.
    const earliest = Math.min(
      8,
      ...meetings.filter((m) => days.some((d) => onDay(m, d))).map((m) => new Date(m.startsAt).getHours()),
    );
    if (scroller.current) scroller.current.scrollTop = Math.max(0, earliest - 0.5) * HOUR_PX;
  }, [dayKey(days[0])]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const hours = Array.from({ length: 24 }, (_, h) => h);
  const hourLabel = (h: number) =>
    new Intl.DateTimeFormat(LOCALE_TAG[locale], { hour: 'numeric' }).format(new Date(2026, 0, 1, h));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[44rem]">
        {/* The days across the top. */}
        <div className="grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))] border-b hairline bg-[var(--surface-sunken)]">
          <div aria-hidden />
          {days.map((day) => {
            const isToday = sameDay(day, today);
            return (
              <div key={dayKey(day)} className="border-s hairline px-2 py-1.5 text-center">
                <span className="block text-overline uppercase text-[var(--text-tertiary)]">
                  {new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'short' }).format(day)}
                </span>
                <span
                  className={clsx(
                    'mx-auto mt-0.5 grid h-7 min-w-7 place-items-center rounded-full px-1 text-body-sm font-semibold tabular',
                    isToday ? 'bg-primary text-white' : 'text-[var(--text-primary)]',
                  )}
                >
                  {day.getDate()}
                </span>
              </div>
            );
          })}
        </div>

        <div ref={scroller} className="max-h-[36rem] overflow-y-auto">
          <div className="relative grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]" style={{ height: 24 * HOUR_PX }}>
            {/* The hours down the side. */}
            <div className="relative" aria-hidden>
              {hours.map((h) => (
                <span
                  key={h}
                  className="absolute end-2 -translate-y-1/2 text-[11px] text-[var(--text-tertiary)] tabular"
                  style={{ top: h * HOUR_PX }}
                >
                  {h > 0 ? hourLabel(h) : ''}
                </span>
              ))}
            </div>

            {days.map((day) => {
              const dayStart = startOfDay(day).getTime();
              const placed = layoutDay(meetings.filter((m) => onDay(m, day)));
              const isToday = sameDay(day, today);
              const past = addDays(day, 1) <= today;
              return (
                <div key={dayKey(day)} className={clsx('relative border-s hairline', isToday && 'bg-primary/[0.03]')}>
                  {/* Each hour a line, and a place to start a meeting at it. */}
                  {hours.map((h) => (
                    <button
                      key={h}
                      type="button"
                      tabIndex={-1}
                      disabled={past}
                      onClick={() => navigate(`/meetings/new?date=${dayKey(day)}&time=${String(h).padStart(2, '0')}:00`)}
                      aria-label={t('meetings.newAt', { time: hourLabel(h) })}
                      className="absolute inset-x-0 border-t hairline enabled:hover:bg-primary/5 disabled:cursor-default"
                      style={{ top: h * HOUR_PX, height: HOUR_PX }}
                    />
                  ))}
                  {placed.map(({ item: m, column, columns }) => {
                    const start = Math.max(dayStart, new Date(m.startsAt).getTime());
                    const end = Math.min(dayStart + 86_400_000, new Date(m.endsAt).getTime());
                    const top = ((start - dayStart) / 3_600_000) * HOUR_PX;
                    const height = Math.max(20, ((end - start) / 3_600_000) * HOUR_PX - 2);
                    const look = meetingLook(m);
                    return (
                      <Link
                        key={m.id}
                        to={`/meetings/${m.id}`}
                        title={chipTitle(m, look, locale, t)}
                        className={clsx(
                          'absolute z-[1] overflow-hidden rounded-sm px-1.5 py-0.5 text-[11px] leading-4 ring-1 ring-inset',
                          'transition-[filter] duration-fast hover:brightness-95 dark:hover:brightness-125',
                          LOOK_CHIP[look],
                        )}
                        style={{
                          top,
                          height,
                          insetInlineStart: `calc(${(column / columns) * 100}% + 2px)`,
                          width: `calc(${100 / columns}% - 4px)`,
                        }}
                      >
                        <bdi className="block truncate font-semibold">{m.title}</bdi>
                        {height > 30 && <span className="block truncate tabular">{meetingTimes(m, locale)}</span>}
                      </Link>
                    );
                  })}
                  {isToday && (
                    <div
                      className="pointer-events-none absolute inset-x-0 z-[2] h-0.5 bg-primary"
                      style={{ top: ((now.getTime() - dayStart) / 3_600_000) * HOUR_PX }}
                      aria-hidden
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* List                                                                        */
/* -------------------------------------------------------------------------- */

/** The month's meetings, day by day — only the days that have one. */
function ListView({ days, meetings }: { days: Date[]; meetings: MeetingRow[] }) {
  const { t, locale } = useI18n();
  const today = startOfDay(new Date());
  const withMeetings = days
    .map((day) => ({ day, list: meetings.filter((m) => sameDay(new Date(m.startsAt), day) || (onDay(m, day) && sameDay(day, days[0]))) }))
    .filter((d) => d.list.length > 0);

  if (withMeetings.length === 0) {
    return <EmptyState icon={<CalendarCheck className="h-8 w-8" />} title={t('meetings.noneMonth')} hint={t('meetings.noneMonthHint')} />;
  }
  return (
    <div className="divide-y divide-[var(--line)]">
      {withMeetings.map(({ day, list }) => (
        <section key={dayKey(day)} className="grid gap-3 p-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
          <h3 className={clsx('text-body-sm font-semibold', sameDay(day, today) ? 'text-primary-600 dark:text-primary-400' : 'text-[var(--text-primary)]')}>
            {new Intl.DateTimeFormat(LOCALE_TAG[locale], { weekday: 'long', day: 'numeric', month: 'long' }).format(day)}
            {sameDay(day, today) && <span className="ms-2 text-caption font-medium">{t('meetings.today')}</span>}
          </h3>
          <ul className="grid gap-2 md:grid-cols-2">
            {list.map((m) => (
              <li key={m.id}>
                <MeetingCard meeting={m} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Legend, refusals                                                            */
/* -------------------------------------------------------------------------- */

function Legend({ wider }: { wider: boolean }) {
  const { t } = useI18n();
  const looks: MeetingLook[] = ['organizer', 'accepted', 'pending', 'declined', ...(wider ? (['viewer'] as const) : []), 'cancelled'];
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-caption text-muted" aria-label={t('meetings.legend')}>
      {looks.map((look) => (
        <li key={look} className="inline-flex items-center gap-1.5">
          <span className={clsx('h-2 w-2 rounded-full', LOOK_DOT[look])} aria-hidden />
          {t(LOOK_LABEL[look])}
        </li>
      ))}
    </ul>
  );
}

/** What to say when the API refuses: in the reader's language where it says why, its own words otherwise. */
export function meetingRefusal(error: unknown, t: (key: TranslationKey, vars?: Record<string, string | number>) => string): string {
  const e = error as { code?: string; details?: { reason?: string } } | null;
  const reason = e?.details?.reason;
  if (e?.code === 'OUTSIDE_YOUR_DEPARTMENTS') {
    return t((e.details as { departmentIds?: string[] } | undefined)?.departmentIds ? 'meetings.refusal.outsideDepartment' : 'meetings.refusal.outside');
  }
  if (e?.code === 'CONCURRENT_CHANGE') return t('meetings.refusal.CONCURRENT_CHANGE');
  if (reason && MEETING_REFUSALS.includes(reason)) return t(`meetings.refusal.${reason}` as TranslationKey);
  return refusalText(error, t);
}

const MEETING_REFUSALS = [
  'CANCELLED',
  'OVER',
  // Its status (2026-10-26): done, started (no more changes), and the moves refused.
  'DONE',
  'STARTED',
  'BACKWARDS',
  'TOO_EARLY',
  'NOT_STARTED',
  'NOBODY',
  'INACTIVE',
  'DEPARTMENT',
  'EVERYONE',
  'INVALID',
  'ENDS_BEFORE_START',
  'TOO_SHORT',
  'TOO_LONG',
  'IN_THE_PAST',
];
